import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { BlockComponent } from "@components/container-block/block.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { LabelComponent } from "@components/label/label.component";
import { ProduktDTO } from "~app/common/models/produkt/ProduktDTO";
import { ProduktlarmDTO } from "~app/common/models/produkt/ProduktlarmDTO";
import { ProduktspecifikationDTO } from "~app/common/models/produkt/ProduktspecifikationDTO";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { ProduktDetailTexts } from "../../produkt-detail.texts";
import { HandelssortimentsgruppKategori } from "../../produkt-details.models";
import { ProduktDetailUppfoljningProduktlarmComponent } from "./produkt-detail-uppfoljning-produktlarm/produkt-detail-uppfoljning-produktlarm.component";
import { ProduktDetailUppfoljningValidation } from "./produkt-detail-uppfoljning.validation";

@Component({
    selector: "rask-produkt-detail-uppfoljning",
    templateUrl: "./produkt-detail-uppfoljning.component.html",
    styleUrls: ["./produkt-detail-uppfoljning.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        BlockComponent,
        InputNumberComponent,
        LabelComponent,
        ProduktDetailUppfoljningProduktlarmComponent
    ]
})
export class ProduktDetailUppfoljningComponent implements OnInit, OnDestroy, OnChanges {
    form: UntypedFormGroup;
    @Input() produkt: ProduktDTO;
    @Input() produktspecifikation: ProduktspecifikationDTO;
    @Input() rootForm: UntypedFormGroup;
    texts = ProduktDetailTexts.uppfoljningTab.texts;
    validation: IValidationScope;
    validationContext: IValidationContext;

    private readonly fieldsToUseInForm = [
        "modultraffMin",
        "modultraffMax",
        "rotandelMax",
        "meddellangdOptimerad"
    ];
    private readonly formBinder = inject(FormBinderService);
    private readonly formId = "produkt-detail-tab-uppfoljning";

    get filteredProduktlarm(): ProduktlarmDTO[] {
        if (this.showLangdfordelningar) {
            const produktlarmTyper = this.getFilteredProduktlarmtyper();
            return this.produktspecifikation.produktlarm
                .filter(x => produktlarmTyper.includes(x.typ));
        }

        return null;
    }

    get isMassaved(): boolean {
        return this.produkt.handelssortimentsgrupp?.kategori === HandelssortimentsgruppKategori.massaved;
    }

    get isSagtimmer(): boolean {
        return this.produkt.handelssortimentsgrupp?.kategori === HandelssortimentsgruppKategori.sagtimmer;
    }

    get showLangdfordelningar(): boolean {
        return !!this.produkt.handelssortimentsgrupp?.kategori;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.shouldRecreateForm(changes)) {
            return;
        }

        this.destroyForm();
        this.createForm();
    }

    ngOnDestroy(): void {
        this.destroyForm();
    }

    ngOnInit(): void {
        this.createForm();
    }

    private createForm(): void {
        this.form = this.formBinder.CreateGroup(this.produktspecifikation, this.fieldsToUseInForm);

        this.validationContext = ProduktDetailUppfoljningValidation.init(this.produktspecifikation);
        this.validationContext.scope(ValidationScopes.spara).subscribe(v => this.validation = v);
        this.formBinder.attachValidation(this.validationContext.current, this.form);
        this.form.markAsPristine();

        this.rootForm.addControl(this.formId, this.form);
    }

    private destroyForm(): void {
        this.formBinder.detachFromRootForm(this.rootForm, this.formId);
    }

    private getFilteredProduktlarmtyper(): string[] {
        if (this.isSagtimmer) {
            return [
                "LANGDFORDELNING",
                "DIAMETERFORDELNING",
                "FORDELNINGSGRAD",
                "VRAKANDEL",
                "MANUELLAKAP"
            ];
        } else if (this.isMassaved) {
            return ["VRAKANDEL"];
        }

        return [
            "FUKTHALTANDEL",
            "FLISSTORLEKANDEL"
        ];
    }

    private shouldRecreateForm(changes: SimpleChanges): boolean {
        if (changes.produkt && changes.produkt.previousValue &&
            changes.produkt.previousValue.rowVersion !== changes.produkt.currentValue.rowVersion) {
            return true;
        }

        if (changes.produktspecifikation && changes.produktspecifikation.previousValue &&
            changes.produktspecifikation.previousValue.rowVersion !== changes.produktspecifikation.currentValue.rowVersion) {
            return true;
        }

        return false;
    }
}
